import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  ClipboardCheck, Banknote, Landmark, AlertTriangle, CheckCircle2, FileDown, RefreshCw,
  Lock, Unlock, History, CalendarDays, CalendarRange, CalendarCheck, Bell, ChevronLeft,
  ChevronRight, Info, ShieldCheck, Wallet, TrendingUp, TrendingDown, Scale
} from 'lucide-react';
import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
import ProTable from '../ui-pro/ProTable';
import { downloadFile } from '../ui-pro/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tl = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const trDate = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('.') : '-');
const todayISO = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const mondayOf = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};
const shiftDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const shiftMonth = (ym, n) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const STATUS_META = {
  closed: { label: 'Kapatıldı', cls: 'bg-emerald-100 text-emerald-800' },
  closed_with_diff: { label: 'Farklı Kapatıldı', cls: 'bg-amber-100 text-amber-800' },
  open: { label: 'Açık', cls: 'bg-slate-100 text-slate-600' },
};
const LEVEL_META = {
  critical: { cls: 'border-rose-200 bg-rose-50', icon: 'text-rose-600', badge: 'bg-rose-100 text-rose-800' },
  warning: { cls: 'border-amber-200 bg-amber-50', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-800' },
  info: { cls: 'border-sky-200 bg-sky-50', icon: 'text-[#176B91]', badge: 'bg-sky-100 text-[#176B91]' },
};

const StatusBadge = ({ status, testid }) => {
  const m = STATUS_META[status] || STATUS_META.open;
  return <Badge className={`text-[10px] ${m.cls} hover:bg-current/10`} data-testid={testid}>{m.label}</Badge>;
};

const WarningList = ({ warnings, onNavigate, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    {warnings.length === 0 ? (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3">
        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        <p className="text-[13px] text-emerald-800">Uyarı yok — tüm kontroller temiz görünüyor.</p>
      </div>
    ) : warnings.map(w => {
      const m = LEVEL_META[w.level] || LEVEL_META.info;
      return (
        <button key={w.key} onClick={() => onNavigate?.(w.target)}
          className={`w-full text-left flex items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors hover:brightness-[0.98] ${m.cls}`}
          data-testid={`warning-${w.key}`}>
          <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${m.icon}`} />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold text-slate-800">{w.title}</p>
            <p className="text-[11px] text-slate-600">{w.detail}</p>
          </div>
          <div className="text-right shrink-0">
            <Badge className={`text-[10px] ${m.badge} hover:bg-current/10`}>{w.count} kayıt</Badge>
            {!!w.amount && <p className="text-[12px] font-semibold text-slate-700 mt-1 tabular-nums">{tl(w.amount)}</p>}
          </div>
        </button>
      );
    })}
  </div>
);

const Checklist = ({ items, onChange, disabled, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
      <Info className="w-3.5 h-3.5 text-[#176B91]" />
      Bu işaretler yalnızca kontrol kaydıdır; muhasebe bakiyelerini değiştirmez.
    </p>
    {items.map((it, i) => (
      <div key={it.key}
        className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${it.checked ? 'border-emerald-200 bg-emerald-50/60' : 'border-[#E2E8F0]'}`}>
        <button type="button" disabled={disabled}
          onClick={() => onChange(items.map((x, j) => (j === i ? { ...x, checked: !x.checked } : x)))}
          className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 ${it.checked ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 bg-white'}`}
          data-testid={`checklist-${it.key}`}>
          {it.checked && <CheckCircle2 className="w-3 h-3" />}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-slate-800">{it.label}</p>
          <Input value={it.note || ''} placeholder="Not (opsiyonel)" disabled={disabled}
            className="h-7 mt-1 text-[12px]"
            onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))}
            data-testid={`checklist-note-${it.key}`} />
        </div>
      </div>
    ))}
  </div>
);

/* ==================== GÜNLÜK ==================== */

const DailyClosing = ({ onNavigate }) => {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counted, setCounted] = useState('');
  const [recon, setRecon] = useState({});
  const [checklist, setChecklist] = useState([]);
  const [diffReason, setDiffReason] = useState('');
  const [acceptDiff, setAcceptDiff] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/closing/daily`, { params: { date } });
      const d = res.data;
      setData(d);
      const c = d.closing;
      setCounted(c ? String(c.counted_cash) : '');
      setChecklist(d.checklist || []);
      setDiffReason(c?.diff_reason || '');
      setNotes(c?.notes || '');
      setAcceptDiff(false);
      const r = {};
      (c?.bank_recon || []).forEach(b => { r[b.key] = { statement: b.statement_balance ?? '', note: b.note || '' }; });
      setRecon(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gün sonu verisi yüklenemedi');
    } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const exp = data?.expected;
  const expectedCash = exp?.expected_cash ?? 0;
  const diff = counted === '' ? null : Math.round((parseFloat(counted) - expectedCash) * 100) / 100;
  const status = data?.status || 'open';
  const isClosed = status !== 'open';
  const bankAccounts = (exp?.accounts || []).filter(a => a.type === 'bank' && !a.info_only && a.is_active !== false);

  const submit = async (draft) => {
    if (counted === '' && !draft) { toast.error('Sayılan nakit tutarını girin'); return; }
    setSaving(true);
    try {
      const body = {
        date,
        counted_cash: parseFloat(counted || 0),
        bank_recon: bankAccounts.map(a => ({
          key: a.key,
          statement_balance: recon[a.key]?.statement === '' || recon[a.key]?.statement === undefined
            ? null : parseFloat(recon[a.key].statement),
          note: recon[a.key]?.note || '',
        })),
        checklist, diff_reason: diffReason, accept_diff: acceptDiff, notes, draft,
      };
      const url = `${API}/closing/daily${isClosed ? `/${date}` : ''}`;
      const res = isClosed ? await axios.put(url, body) : await axios.post(url, body);
      toast.success(draft ? 'Taslak kaydedildi' : res.data.status === 'closed_with_diff'
        ? 'Gün farkla kapatıldı (fark kaydedildi)' : 'Gün sonu kapanışı tamamlandı');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    } finally { setSaving(false); }
  };

  const resolveDiff = async () => {
    const note = window.prompt('Kasa farkı nasıl çözüldü? (zorunlu açıklama)');
    if (!note) return;
    try {
      await axios.post(`${API}/closing/daily/${date}/resolve-diff`, { note });
      toast.success('Fark çözüldü olarak işaretlendi');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'İşaretlenemedi'); }
  };

  const reopen = async () => {
    if (!window.confirm('Kapanışı yeniden açmak istiyor musunuz? İşlem geçmişe kaydedilir.')) return;
    try {
      await axios.post(`${API}/closing/daily/${date}/reopen`);
      toast.success('Kapanış yeniden açıldı');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Açılamadı'); }
  };

  return (
    <div className="space-y-4" data-testid="daily-closing">
      <PageHeader
        icon={ClipboardCheck}
        title="Gün Sonu Kontrolü"
        description={`${trDate(date)} — beklenen tutarlar gerçek gelir/gider kayıtlarından hesaplanır`}
        testid="daily-closing-header"
        actions={
          <>
            <StatusBadge status={status} testid="daily-status-badge" />
            <Button variant="outline" size="sm" className="h-9 px-2" onClick={() => setDate(shiftDays(date, -1))} data-testid="daily-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Input type="date" value={date} className="h-9 w-[150px]"
              onChange={(e) => setDate(e.target.value)} data-testid="daily-date-input" />
            <Button variant="outline" size="sm" className="h-9 px-2" onClick={() => setDate(shiftDays(date, 1))} data-testid="daily-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="h-9" onClick={() => setDate(todayISO())} data-testid="daily-today">Bugün</Button>
            <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid="daily-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" className="h-9"
              onClick={() => downloadFile(`${API}/closing/export`, { period: 'daily', date }, `gun-sonu-${date}.pdf`)}
              data-testid="daily-pdf-btn">
              <FileDown className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Wallet} label="Ay Başı Devir (Nakit)" value={tl(exp?.opening_cash)} tone="slate"
          sub={`Tüm zamanlar: ${tl(exp?.opening_cash_all)}`} testid="daily-kpi-opening" />
        <StatCard icon={TrendingUp} label="Günün Nakit Geliri" value={tl(exp?.day_cash_income)} tone="emerald" testid="daily-kpi-income" />
        <StatCard icon={TrendingDown} label="Günün Nakit Gideri" value={tl(exp?.day_cash_expense)} tone="rose" testid="daily-kpi-expense" />
        <StatCard icon={Banknote} label="Beklenen Nakit (Bu Ay)" value={tl(expectedCash)} tone="indigo"
          sub={`Tüm zamanlar: ${tl(exp?.expected_cash_all)}`} testid="daily-kpi-expected" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        {/* Nakit sayımı */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="bg-emerald-50 text-emerald-700 rounded-lg p-2"><Banknote className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-bold text-slate-900">Nakit Sayımı</p>
              <p className="text-[11px] text-slate-500">Kasada fiilen bulunan tutarı girin</p>
            </div>
          </div>
          <div>
            <Label className="text-[12px]">Sayılan (fiilî) nakit *</Label>
            <Input type="number" step="0.01" value={counted} placeholder="0,00"
              onChange={(e) => setCounted(e.target.value)} className="h-10 text-base"
              data-testid="counted-cash-input" />
          </div>
          <div className={`rounded-lg border px-3 py-2.5 ${diff === null ? 'border-[#E2E8F0]' : Math.abs(diff) < 0.01 ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'}`}
            data-testid="cash-diff-box">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-slate-600">Beklenen (bu ay)</span>
              <span className="text-[13px] font-semibold tabular-nums">{tl(expectedCash)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-400">Tüm zamanlar</span>
              <span className="text-[11px] text-slate-400 tabular-nums" data-testid="cash-expected-all">{tl(exp?.expected_cash_all)}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] text-slate-600">Sayılan</span>
              <span className="text-[13px] font-semibold tabular-nums">{counted === '' ? '-' : tl(counted)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-slate-200 mt-1.5 pt-1.5">
              <span className="text-[12px] font-semibold text-slate-700 flex items-center gap-1">
                <Scale className="w-3.5 h-3.5" /> Fark
              </span>
              <span className={`text-base font-bold tabular-nums ${diff === null ? 'text-slate-400' : Math.abs(diff) < 0.01 ? 'text-emerald-700' : 'text-rose-700'}`}
                data-testid="cash-diff-value">
                {diff === null ? '-' : `${diff > 0 ? '+' : ''}${tl(diff)}`}
              </span>
            </div>
          </div>
          {diff !== null && Math.abs(diff) >= 0.01 && (
            <div className="space-y-2">
              <div>
                <Label className="text-[12px] text-rose-700">Fark açıklaması (zorunlu) *</Label>
                <Textarea rows={2} value={diffReason} onChange={(e) => setDiffReason(e.target.value)}
                  placeholder="Farkın nedeni ve yapılan kontrol" data-testid="diff-reason-input" />
              </div>
              <button type="button" onClick={() => setAcceptDiff(v => !v)}
                className={`w-full flex items-start gap-2 rounded-lg border px-3 py-2 text-left ${acceptDiff ? 'border-amber-300 bg-amber-50' : 'border-[#E2E8F0]'}`}
                data-testid="accept-diff-toggle">
                <AlertTriangle className={`w-4 h-4 mt-0.5 ${acceptDiff ? 'text-amber-700' : 'text-slate-400'}`} />
                <p className="text-[12px] text-slate-700">
                  <b>Farkı onaylayarak kapat.</b> Fark çözülmeden olağan kapanış yapılamaz; onaylarsanız gün
                  "Farklı Kapatıldı" olarak kaydedilir ve fark takibe alınır.
                </p>
              </button>
            </div>
          )}
          {data?.closing?.status === 'closed_with_diff' && !data?.closing?.diff_resolved && (
            <Button variant="outline" size="sm" className="w-full h-9 text-amber-800 border-amber-300"
              onClick={resolveDiff} data-testid="resolve-diff-btn">
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Farkı çözüldü olarak işaretle
            </Button>
          )}
        </div>

        {/* Banka mutabakatı */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="bg-sky-50 text-[#176B91] rounded-lg p-2"><Landmark className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-bold text-slate-900">Banka Mutabakatı</p>
              <p className="text-[11px] text-slate-500">Her hesap için ekstre bakiyesini girin</p>
            </div>
          </div>
          <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
            {bankAccounts.map(a => {
              const st = recon[a.key]?.statement;
              const bd = st === '' || st === undefined ? null : Math.round((parseFloat(st) - a.expected) * 100) / 100;
              return (
                <div key={a.key} className="rounded-lg border border-[#E2E8F0] px-3 py-2" data-testid={`bank-recon-${a.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-semibold text-slate-800 truncate">{a.name}</p>
                    <span className="text-[12px] text-slate-500 tabular-nums">Beklenen {tl(a.expected)}</span>
                  </div>
                  <p className="text-[10px] text-slate-400 tabular-nums">Bu ay bazlı · tüm zamanlar: {tl(a.expected_all)}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input type="number" step="0.01" placeholder="Ekstre bakiyesi" className="h-8 text-[12px]"
                      value={st ?? ''}
                      onChange={(e) => setRecon(r => ({ ...r, [a.key]: { ...r[a.key], statement: e.target.value } }))}
                      data-testid={`bank-statement-${a.key}`} />
                    <span className={`text-[12px] font-bold tabular-nums w-[110px] text-right ${bd === null ? 'text-slate-400' : Math.abs(bd) < 0.01 ? 'text-emerald-700' : 'text-rose-700'}`}
                      data-testid={`bank-diff-${a.key}`}>
                      {bd === null ? 'fark -' : `${bd > 0 ? '+' : ''}${tl(bd)}`}
                    </span>
                  </div>
                  {bd !== null && Math.abs(bd) >= 0.01 && (
                    <Input placeholder="Fark açıklaması (zorunlu)" className="h-8 mt-1.5 text-[12px]"
                      value={recon[a.key]?.note || ''}
                      onChange={(e) => setRecon(r => ({ ...r, [a.key]: { ...r[a.key], note: e.target.value } }))}
                      data-testid={`bank-note-${a.key}`} />
                  )}
                </div>
              );
            })}
            {(exp?.accounts || []).filter(a => a.info_only).map(a => (
              <div key={a.key} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                data-testid={`info-account-${a.key}`}>
                <p className="text-[12px] font-semibold text-amber-900">{a.name} · bilgi amaçlı</p>
                <p className="text-[11px] text-amber-800">
                  Günün hareketi {tl(a.income - a.expense)} — kasa/banka toplamlarına yansımaz.
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Uyarılar */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="bg-amber-50 text-amber-700 rounded-lg p-2"><AlertTriangle className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-bold text-slate-900">Kontrol Uyarıları</p>
              <p className="text-[11px] text-slate-500">Gerçek kayıtlardan otomatik üretildi</p>
            </div>
          </div>
          <div className="max-h-[360px] overflow-y-auto pr-1">
            <WarningList warnings={data?.warnings || []} onNavigate={onNavigate} testid="daily-warnings" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="xl:col-span-2 bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4 space-y-3">
          <p className="text-sm font-bold text-slate-900">Kontrol Listesi</p>
          <Checklist items={checklist} onChange={setChecklist} testid="daily-checklist" />
          <div>
            <Label className="text-[12px]">Gün sonu notu</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Devir, teslim veya dikkat edilmesi gereken hususlar" data-testid="daily-notes" />
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="outline" className="h-10" disabled={saving} onClick={() => submit(true)}
              data-testid="daily-draft-btn">Taslak Kaydet</Button>
            <Button className="h-10 bg-[#0B172A] hover:bg-[#13233C]" disabled={saving} onClick={() => submit(false)}
              data-testid="daily-close-btn">
              <Lock className="w-4 h-4 mr-1.5" /> {isClosed ? 'Düzeltmeyi Kaydet' : 'Günü Kapat'}
            </Button>
            {isClosed && (
              <Button variant="outline" className="h-10 text-rose-700 border-rose-200" onClick={reopen}
                data-testid="daily-reopen-btn">
                <Unlock className="w-4 h-4 mr-1.5" /> Kapanışı Aç
              </Button>
            )}
            {isClosed && (
              <p className="text-[12px] text-slate-500">
                Kapatan: <b>{data.closing?.closed_by || '-'}</b> · {(data.closing?.closed_at || '').slice(0, 16).replace('T', ' ')}
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4 space-y-2">
          <p className="text-sm font-bold text-slate-900 flex items-center gap-2"><History className="w-4 h-4" /> Değişiklik Geçmişi</p>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1" data-testid="daily-history">
            {(data?.closing?.history || []).length === 0
              ? <p className="text-[12px] text-slate-400 py-6 text-center">Henüz işlem yok</p>
              : [...(data.closing.history || [])].reverse().map((h, i) => (
                <div key={i} className="rounded-lg border border-[#E2E8F0] px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <Badge className="text-[10px] bg-slate-100 text-slate-700 hover:bg-slate-100">
                      {h.action === 'close' ? 'Kapanış' : h.action === 'draft' ? 'Taslak'
                        : h.action === 'reopen' ? 'Yeniden açıldı' : h.action === 'resolve_diff' ? 'Fark çözüldü' : h.action}
                    </Badge>
                    <span className="text-[11px] text-slate-500">{(h.at || '').slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <p className="text-[12px] text-slate-700 mt-1">{h.by}</p>
                  {h.note && <p className="text-[11px] text-slate-500">{h.note}</p>}
                  {h.changes && Object.keys(h.changes).length > 0 && (
                    <ul className="text-[11px] text-slate-500 mt-0.5 space-y-0.5">
                      {Object.entries(h.changes).map(([f, v]) => (
                        <li key={f}>{f}: <s>{String(v[0] ?? '-')}</s> → <b>{String(v[1] ?? '-')}</b></li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
          </div>
          <div className="pt-1 border-t border-[#E2E8F0]">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Son Kapanışlar</p>
            <div className="space-y-1" data-testid="recent-closings">
              {(data?.recent_closings || []).slice(0, 6).map(c => (
                <button key={c.date} onClick={() => setDate(c.date)}
                  className="w-full flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-slate-50 text-left"
                  data-testid={`recent-closing-${c.date}`}>
                  <span className="text-[12px] text-slate-700">{trDate(c.date)}</span>
                  <span className="flex items-center gap-1.5">
                    {!!c.cash_diff && <span className="text-[11px] text-rose-700 tabular-nums">{tl(c.cash_diff)}</span>}
                    <StatusBadge status={c.status} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==================== HAFTALIK / AYLIK ==================== */

const PeriodClosing = ({ kind, onNavigate }) => {
  const [key, setKey] = useState(kind === 'weekly' ? mondayOf(todayISO()) : todayISO().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = kind === 'weekly' ? { week_start: key } : { month: key };
      const res = await axios.get(`${API}/closing/${kind}`, { params });
      setData(res.data);
      setChecklist(res.data.checklist || []);
      setNotes(res.data.saved?.notes || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dönem verisi yüklenemedi');
    } finally { setLoading(false); }
  }, [kind, key]);

  useEffect(() => { load(); }, [load]);

  const submit = async (draft) => {
    setSaving(true);
    try {
      await axios.post(`${API}/closing/${kind}`, { key, checklist, notes, draft });
      toast.success(draft ? 'Taslak kaydedildi' : kind === 'weekly' ? 'Hafta kapatıldı' : 'Ay kapatıldı');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kapatılamadı');
    } finally { setSaving(false); }
  };

  const s = data?.summary;
  const title = kind === 'weekly' ? 'Haftalık Finansal Kontrol' : 'Aylık Muhasebe Kapanışı';
  const dayColumns = useMemo(() => [
    { key: 'date', label: 'Tarih', sortable: true, width: 110, render: (r) => trDate(r.date) },
    { key: 'income', label: 'Gelir', align: 'right', sortable: true, render: (r) => <span className="text-emerald-700 font-semibold tabular-nums">{tl(r.income)}</span> },
    { key: 'expense', label: 'Gider', align: 'right', sortable: true, render: (r) => <span className="text-rose-700 font-semibold tabular-nums">{tl(r.expense)}</span> },
    { key: 'net', label: 'Net', align: 'right', sortable: true, render: (r) => <span className={`font-semibold tabular-nums ${r.net < 0 ? 'text-rose-700' : 'text-slate-900'}`}>{tl(r.net)}</span> },
    { key: 'status', label: 'Gün Sonu', align: 'center', sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: 'cash_diff', label: 'Kasa Farkı', align: 'right', sortable: true, render: (r) => (r.cash_diff ? <span className="text-rose-700 font-semibold tabular-nums">{tl(r.cash_diff)}</span> : <span className="text-slate-400">-</span>) },
    { key: 'closed_by', label: 'Kapatan', render: (r) => <span className="text-[12px] text-slate-600">{r.closed_by || '-'}</span> },
  ], []);

  return (
    <div className="space-y-4" data-testid={`${kind}-closing`}>
      <PageHeader
        icon={kind === 'weekly' ? CalendarRange : CalendarCheck}
        title={title}
        description={data ? `${trDate(data.start)} – ${trDate(data.end)}` : '-'}
        testid={`${kind}-header`}
        actions={
          <>
            <StatusBadge status={data?.status} testid={`${kind}-status-badge`} />
            <Button variant="outline" size="sm" className="h-9 px-2"
              onClick={() => setKey(kind === 'weekly' ? shiftDays(key, -7) : shiftMonth(key, -1))}
              data-testid={`${kind}-prev`}><ChevronLeft className="w-4 h-4" /></Button>
            {kind === 'weekly'
              ? <Input type="date" value={key} className="h-9 w-[150px]"
                  onChange={(e) => setKey(mondayOf(e.target.value))} data-testid="weekly-date-input" />
              : <Input type="month" value={key} className="h-9 w-[150px]"
                  onChange={(e) => setKey(e.target.value)} data-testid="monthly-month-input" />}
            <Button variant="outline" size="sm" className="h-9 px-2"
              onClick={() => setKey(kind === 'weekly' ? shiftDays(key, 7) : shiftMonth(key, 1))}
              data-testid={`${kind}-next`}><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" className="h-9" onClick={load} data-testid={`${kind}-refresh`}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button variant="outline" size="sm" className="h-9"
              onClick={() => downloadFile(`${API}/closing/export`,
                kind === 'weekly' ? { period: 'weekly', week_start: key } : { period: 'monthly', month: key },
                `${kind === 'weekly' ? 'haftalik-kontrol' : 'aylik-kapanis'}-${key}.pdf`)}
              data-testid={`${kind}-pdf-btn`}>
              <FileDown className="w-4 h-4 mr-1.5" /> PDF
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={TrendingUp} label="Dönem Geliri" value={tl(s?.income)} tone="emerald" testid={`${kind}-kpi-income`} />
        <StatCard icon={TrendingDown} label="Dönem Gideri" value={tl(s?.expense)} tone="rose" testid={`${kind}-kpi-expense`} />
        <StatCard icon={Scale} label="Net Sonuç" value={tl(s?.net)} tone={(s?.net || 0) < 0 ? 'rose' : 'indigo'} testid={`${kind}-kpi-net`} />
        <StatCard icon={ClipboardCheck} label="Kapatılan Gün"
          value={`${s?.closed_days ?? 0}/${s?.total_days ?? 0}`} tone="slate"
          sub={s?.unresolved_diffs ? `${s.unresolved_diffs} çözülmemiş fark` : 'fark yok'}
          testid={`${kind}-kpi-days`} />
        <StatCard icon={Banknote} label="Güncel Nakit" value={tl(s?.cash_total)} tone="emerald" testid={`${kind}-kpi-cash`} />
        <StatCard icon={Landmark} label="Güncel Banka" value={tl(s?.bank_total)} tone="sky" testid={`${kind}-kpi-bank`} />
        <StatCard icon={Wallet} label="Toplam Varlık" value={tl(s?.grand_total)} tone="indigo" testid={`${kind}-kpi-total`} />
        <StatCard icon={AlertTriangle} label="Toplam Kasa Farkı" value={tl(s?.diff_total)} tone="amber" testid={`${kind}-kpi-diff`} />
      </div>

      <ProTable
        testid={`${kind}-days-table`}
        columns={dayColumns}
        rows={data?.days || []}
        rowKey={(r) => r.date}
        defaultPageSize={50}
        emptyText="Bu dönemde kayıt yok"
        footer={(visible) => (
          <tr>
            {visible.map(c => (
              <td key={c.key} className={`px-3 py-2 text-[12px] font-bold ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.key === 'date' ? 'TOPLAM'
                  : c.key === 'income' ? tl(s?.income)
                    : c.key === 'expense' ? tl(s?.expense)
                      : c.key === 'net' ? tl(s?.net)
                        : c.key === 'cash_diff' ? tl(s?.diff_total) : ''}
              </td>
            ))}
          </tr>
        )}
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4">
          <p className="text-sm font-bold text-slate-900 mb-2">Hesap Bazlı Hareket</p>
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1" data-testid={`${kind}-accounts`}>
            {(data?.accounts || []).map(a => (
              <div key={a.key} className={`rounded-lg border px-3 py-2 ${a.info_only ? 'border-amber-200 bg-amber-50' : 'border-[#E2E8F0]'}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[13px] font-semibold text-slate-800 truncate">
                    {a.name}{a.info_only && <span className="text-[10px] text-amber-800 ml-1">(bilgi amaçlı)</span>}
                  </p>
                  <span className="text-[13px] font-bold tabular-nums">{tl(a.closing)}</span>
                </div>
                <p className="text-[11px] text-slate-500">
                  Devir {tl(a.opening)} · Gelir {tl(a.income)} · Gider {tl(a.expense)}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4">
          <p className="text-sm font-bold text-slate-900 mb-2">Kategori Kırılımı</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            <p className="text-[11px] font-semibold text-emerald-700 uppercase">Gelir</p>
            {(data?.income_categories || []).slice(0, 8).map(c => (
              <div key={`i-${c.category}`} className="flex items-center justify-between text-[12px]">
                <span className="text-slate-600 truncate">{c.category} ({c.count})</span>
                <span className="font-semibold text-emerald-700 tabular-nums">{tl(c.total)}</span>
              </div>
            ))}
            <p className="text-[11px] font-semibold text-rose-700 uppercase pt-1">Gider</p>
            {(data?.expense_categories || []).slice(0, 8).map(c => (
              <div key={`e-${c.category}`} className="flex items-center justify-between text-[12px]">
                <span className="text-slate-600 truncate">{c.category} ({c.count})</span>
                <span className="font-semibold text-rose-700 tabular-nums">{tl(c.total)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4 space-y-3">
          <p className="text-sm font-bold text-slate-900">Dönem Kontrolleri</p>
          <div className="max-h-[220px] overflow-y-auto pr-1">
            <Checklist items={checklist} onChange={setChecklist} testid={`${kind}-checklist`} />
          </div>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Dönem notu" data-testid={`${kind}-notes`} />
          <div className="flex items-center gap-2">
            <Button variant="outline" className="h-9" disabled={saving} onClick={() => submit(true)}
              data-testid={`${kind}-draft-btn`}>Taslak</Button>
            <Button className="h-9 bg-[#0B172A] hover:bg-[#13233C]" disabled={saving} onClick={() => submit(false)}
              data-testid={`${kind}-close-btn`}>
              <Lock className="w-4 h-4 mr-1.5" /> {kind === 'weekly' ? 'Haftayı Kapat' : 'Ayı Kapat'}
            </Button>
          </div>
          {data?.saved?.closed_by && (
            <p className="text-[11px] text-slate-500">
              Kapatan: <b>{data.saved.closed_by}</b> · {(data.saved.closed_at || '').slice(0, 16).replace('T', ' ')}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4">
        <p className="text-sm font-bold text-slate-900 mb-2">Dönem Uyarıları</p>
        <WarningList warnings={data?.warnings || []} onNavigate={onNavigate} testid={`${kind}-warnings`} />
      </div>
    </div>
  );
};

/* ==================== HATIRLATMA AYARLARI ==================== */

const ReminderSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/closing/settings`).then(r => setS(r.data)).catch(() => toast.error('Ayarlar yüklenemedi'));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/closing/settings`, s);
      setS(res.data);
      toast.success('Hatırlatma ayarları kaydedildi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  if (!s) return <p className="text-sm text-slate-400 py-10 text-center">Yükleniyor...</p>;
  const toggle = (k) => setS(v => ({ ...v, [k]: !v[k] }));
  const rows = [
    { on: 'cash_count_reminder_enabled', time: 'cash_count_reminder_time', label: 'Günlük nakit sayım hatırlatması', desc: 'Her gün belirtilen saatte (kapanış yapılmamışsa)' },
    { on: 'closing_reminder_enabled', time: 'closing_reminder_time', label: 'Gün sonu kapanış hatırlatması', desc: 'Kapanış yapılmadıysa WhatsApp ile uyarı' },
    { on: 'detailed_check_enabled', time: 'detailed_check_time', label: 'Detaylı nakit kontrolü', desc: 'Belirlenen gün aralığında (örn. 2 günde bir)' },
    { on: 'weekly_reminder_enabled', time: 'weekly_reminder_time', label: 'Haftalık finansal kontrol (Pazar)', desc: 'Her pazar haftalık kontrol hatırlatması' },
    { on: 'monthly_reminder_enabled', time: 'monthly_reminder_time', label: 'Aylık muhasebe kapanışı', desc: 'Ayın son günü hatırlatma' },
  ];

  return (
    <div className="space-y-4" data-testid="closing-settings">
      <PageHeader icon={Bell} title="Hatırlatma Ayarları"
        description="Kapanış ve kasa kontrolü hatırlatmaları WhatsApp ile yönetici numarasına gönderilir"
        testid="closing-settings-header"
        actions={
          <Button className="h-9 bg-[#0B172A] hover:bg-[#13233C]" disabled={saving} onClick={save}
            data-testid="closing-settings-save">Kaydet</Button>
        } />
      <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-4 space-y-3 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[12px]">Yönetici WhatsApp numarası</Label>
            <Input value={s.admin_phone || ''} placeholder="+905XXXXXXXXX"
              onChange={(e) => setS(v => ({ ...v, admin_phone: e.target.value }))}
              data-testid="settings-admin-phone" />
            <p className="text-[11px] text-slate-500 mt-1">Zorunlu format: +90 ile başlayan 12 haneli numara.</p>
          </div>
          <div>
            <Label className="text-[12px]">Detaylı kontrol sıklığı (gün)</Label>
            <Input type="number" min="1" value={s.detailed_check_every_days || 2}
              onChange={(e) => setS(v => ({ ...v, detailed_check_every_days: Number(e.target.value) }))}
              data-testid="settings-detailed-days" />
          </div>
        </div>
        {rows.map(r => (
          <div key={r.on} className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] px-3 py-2.5">
            <button type="button" onClick={() => toggle(r.on)}
              className={`w-10 h-5 rounded-full relative transition-colors shrink-0 ${s[r.on] ? 'bg-emerald-500' : 'bg-slate-300'}`}
              data-testid={`settings-toggle-${r.on}`}>
              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${s[r.on] ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-slate-800">{r.label}</p>
              <p className="text-[11px] text-slate-500">{r.desc}</p>
            </div>
            <Input type="time" value={s[r.time] || '21:00'} className="h-9 w-[110px]"
              onChange={(e) => setS(v => ({ ...v, [r.time]: e.target.value }))}
              data-testid={`settings-time-${r.on}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ==================== KABUK ==================== */

const VIEWS = [
  { id: 'daily', label: 'Gün Sonu', icon: CalendarDays },
  { id: 'weekly', label: 'Haftalık Kontrol', icon: CalendarRange },
  { id: 'monthly', label: 'Aylık Kapanış', icon: CalendarCheck },
  { id: 'settings', label: 'Hatırlatmalar', icon: Bell },
];

const ClosingTab = ({ onNavigate }) => {
  const [view, setView] = useState('daily');
  const navigate = (target) => {
    if (!target || target === 'closing') return;
    const map = { reservations: 'reservations', organizations: 'organizations', customers: 'customers', referees: 'referees' };
    if (map[target]) onNavigate?.(map[target]);
  };

  return (
    <div className="space-y-4" data-testid="closing-tab">
      <div className="flex flex-wrap items-center gap-1.5 bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-1.5">
        {VIEWS.map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-[13px] font-medium transition-colors ${view === v.id ? 'bg-[#0B172A] text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid={`closing-view-${v.id}`}>
            <v.icon className="w-4 h-4" /> {v.label}
          </button>
        ))}
      </div>
      {view === 'daily' && <DailyClosing onNavigate={navigate} />}
      {view === 'weekly' && <PeriodClosing kind="weekly" onNavigate={navigate} />}
      {view === 'monthly' && <PeriodClosing kind="monthly" onNavigate={navigate} />}
      {view === 'settings' && <ReminderSettings />}
    </div>
  );
};

export default ClosingTab;
